import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RefreshCw, Search, MapPin, Phone, ChevronDown, Users, Building2, Globe, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const API = '/api';

// Per-sender fields that fill the signature from each user's Entra/Graph
// profile. When blank, the rendered signature drops that line — so a user with
// an empty Job Title / Mobile / Phone ships a half-empty signature. We surface
// this in the directory so admins can fix the profile before enabling the user.
const SIGNATURE_FIELDS = [
  { key: "jobTitle", label: "Job Title", get: (u) => u.jobTitle },
  { key: "mobilePhone", label: "Mobile", get: (u) => u.mobilePhone },
  { key: "businessPhones", label: "Phone", get: (u) => (Array.isArray(u.businessPhones) ? u.businessPhones[0] : u.businessPhones) },
];

const missingFieldsFor = (user) =>
  SIGNATURE_FIELDS.filter((f) => !String(f.get(user) ?? "").trim()).map((f) => f.label);

export default function StaffDirectory() {
  const [users, setUsers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [search, setSearch] = useState("");
  const [filterMode, setFilterMode] = useState("all");
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkScope, setBulkScope] = useState("all_active");
  const [bulkScopeValue, setBulkScopeValue] = useState("");
  const [bulkFull, setBulkFull] = useState("");
  const [bulkShort, setBulkShort] = useState("");

  const fetchData = useCallback(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/graph/users`),
      axios.get(`${API}/templates/`),
    ]).then(([uRes, tRes]) => {
      setUsers(uRes.data.users);
      setTemplates(tRes.data.templates || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const syncUsers = async () => {
    setSyncing(true);
    await axios.post(`${API}/graph/sync`);
    fetchData();
    setSyncing(false);
  };

  const toggleActive = async (user) => {
    const newVal = !user.signature_active;
    try {
      await axios.patch(`${API}/graph/users/${user.id}/signature-active`, { signature_active: newVal });
      setUsers(prev => prev.map(u => u.id === user.id ? { ...u, signature_active: newVal } : u));
      toast.success(`${user.displayName}: signature ${newVal ? "enabled" : "disabled"}`);
    } catch (err) {
      toast.error(`Failed: ${err.response?.data?.detail || err.message}`);
    }
  };

  const assignTemplate = async (userId, field, templateId) => {
    try {
      // CRITICAL: only send the field being changed. Sending both with the
      // other one empty would silently erase the user's other template
      // (real production data-loss bug fixed 2026-05-30).
      const body = field === "full"
        ? { full_template_id: templateId }
        : { short_template_id: templateId };
      await axios.patch(`${API}/graph/users/${userId}/templates`, body);
      setUsers(prev => prev.map(u => u.id === userId
        ? { ...u, [field === "full" ? "assigned_full_template" : "assigned_short_template"]: templateId }
        : u));
      toast.success("Template assigned");
    } catch (err) {
      toast.error(`Failed: ${err.response?.data?.detail || err.message}`);
    }
  };

  const runMigration = async () => {
    try {
      const res = await axios.post(`${API}/graph/users/migrate-field-names`);
      toast.success(`Migrated ${res.data.migrated} records (initialised signature_active on ${res.data.signature_active_initialised})`);
      fetchData();
    } catch (err) {
      toast.error(`Migration failed: ${err.response?.data?.detail || err.message}`);
    }
  };

  const bulkAssign = async () => {
    try {
      const res = await axios.post(`${API}/graph/users/bulk-templates`, {
        scope: bulkScope,
        scope_value: bulkScopeValue,
        full_template_id: bulkFull,
        short_template_id: bulkShort,
      });
      toast.success(`Templates assigned to ${res.data.modified} users`);
      setBulkOpen(false);
      fetchData();
    } catch (err) {
      toast.error(`Failed: ${err.response?.data?.detail || err.message}`);
    }
  };

  const fullTemplates = templates.filter(t => (t.mode || "").toUpperCase() === "FULL");
  const shortTemplates = templates.filter(t => (t.mode || "").toUpperCase() === "SHORT");

  // Build the <option> list for a row's dropdown. Always include the currently
  // assigned template as an option even if it isn't in the mode-filtered list —
  // otherwise a native <select> whose value matches no option silently renders
  // blank ("None"), making a perfectly-saved assignment LOOK like it reverted on
  // refresh. This was the real cause of the "Short Signature doesn't persist" bug:
  // the saved value was intact in the DB but its <option> was missing from the UI.
  const optionsFor = (mode, assignedId) => {
    const filtered = mode === "FULL" ? fullTemplates : shortTemplates;
    const opts = filtered.map(t => ({ id: t.id, name: t.name }));
    if (assignedId && !filtered.some(t => t.id === assignedId)) {
      const found = templates.find(t => t.id === assignedId);
      opts.unshift({
        id: assignedId,
        name: found ? `${found.name} (${found.mode || "?"})` : "⚠ Removed template",
      });
    }
    return opts;
  };

  const domains = [...new Set(users.map(u => (u.mail || "").split("@")[1]).filter(Boolean))].sort();
  const companies = [...new Set(users.map(u => u.companyName).filter(Boolean))].sort();

  const filtered = users
    .filter((u) => {
      const q = search.toLowerCase();
      const matchesSearch = !q || (
        u.displayName?.toLowerCase().includes(q) ||
        u.mail?.toLowerCase().includes(q) ||
        u.department?.toLowerCase().includes(q) ||
        u.jobTitle?.toLowerCase().includes(q) ||
        u.companyName?.toLowerCase().includes(q)
      );
      const matchesFilter = filterMode === "all" ||
        (filterMode === "active" && u.signature_active) ||
        (filterMode === "inactive" && !u.signature_active) ||
        (filterMode === "incomplete" && missingFieldsFor(u).length > 0);
      return matchesSearch && matchesFilter;
    })
    .sort((a, b) => {
      if (a.signature_active && !b.signature_active) return -1;
      if (!a.signature_active && b.signature_active) return 1;
      return (a.displayName || "").localeCompare(b.displayName || "");
    });

  const activeCount = users.filter(u => u.signature_active).length;
  const incompleteActiveCount = users.filter(u => u.signature_active && missingFieldsFor(u).length > 0).length;

  return (
    <div className="p-6 lg:p-8 max-w-[1600px]" data-testid="staff-directory-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight text-[#171717]">Staff Directory</h1>
          <p className="text-sm text-[#5E5E5E] mt-1">
            {users.length} users &middot; <span className="text-[#107C10] font-medium">{activeCount} active</span> &middot; {users.length - activeCount} inactive
            {incompleteActiveCount > 0 && (
              <>
                {" "}&middot;{" "}
                <button onClick={() => setFilterMode("incomplete")}
                  className="text-[#A4520E] font-medium inline-flex items-center gap-1 hover:underline"
                  data-testid="incomplete-summary-btn">
                  <AlertTriangle size={12} /> {incompleteActiveCount} active with incomplete profile
                </button>
              </>
            )}
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={runMigration} variant="outline"
            className="border-[#107C10] text-[#107C10] rounded-sm text-xs" data-testid="migrate-fields-btn">
            <RefreshCw size={13} className="mr-1.5" /> Repair Legacy Fields
          </Button>
          <Button onClick={() => setBulkOpen(!bulkOpen)} variant="outline"
            className="border-[#0F6CBD] text-[#0F6CBD] rounded-sm text-xs" data-testid="bulk-assign-btn">
            <Users size={13} className="mr-1.5" /> Bulk Assign Templates
          </Button>
          <Button onClick={syncUsers} disabled={syncing}
            className="bg-[#F3F2F1] text-[#171717] hover:bg-[#E1DFDD] rounded-sm text-xs" data-testid="sync-users-btn">
            <RefreshCw size={13} className={`mr-1.5 ${syncing ? "animate-spin" : ""}`} />
            {syncing ? "Syncing..." : "Sync Directory"}
          </Button>
        </div>
      </div>

      {/* Bulk Assign Panel */}
      {bulkOpen && (
        <div className="mt-4 bg-white border-2 border-[#0F6CBD] rounded-sm p-5" data-testid="bulk-assign-panel">
          <h3 className="text-sm font-medium text-[#171717] mb-3">Bulk Assign Templates</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <label className="text-[10px] font-medium text-[#5E5E5E] uppercase mb-1 block">Scope</label>
              <select value={bulkScope} onChange={e => { setBulkScope(e.target.value); setBulkScopeValue(""); }}
                className="w-full text-xs border border-[#E1DFDD] rounded-sm px-2 py-1.5 bg-white" data-testid="bulk-scope">
                <option value="all_active">All Active Users</option>
                <option value="domain">By Domain</option>
                <option value="company">By Company</option>
              </select>
              {bulkScope === "domain" && (
                <select value={bulkScopeValue} onChange={e => setBulkScopeValue(e.target.value)}
                  className="w-full text-xs border border-[#E1DFDD] rounded-sm px-2 py-1.5 bg-white mt-1.5" data-testid="bulk-domain">
                  <option value="">Select domain...</option>
                  {domains.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              )}
              {bulkScope === "company" && (
                <select value={bulkScopeValue} onChange={e => setBulkScopeValue(e.target.value)}
                  className="w-full text-xs border border-[#E1DFDD] rounded-sm px-2 py-1.5 bg-white mt-1.5" data-testid="bulk-company">
                  <option value="">Select company...</option>
                  {companies.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              )}
            </div>
            <div>
              <label className="text-[10px] font-medium text-[#5E5E5E] uppercase mb-1 block">Full Signature Template</label>
              <select value={bulkFull} onChange={e => setBulkFull(e.target.value)}
                className="w-full text-xs border border-[#E1DFDD] rounded-sm px-2 py-1.5 bg-white" data-testid="bulk-full-template">
                <option value="">— No change —</option>
                {fullTemplates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-medium text-[#5E5E5E] uppercase mb-1 block">Short Signature Template</label>
              <select value={bulkShort} onChange={e => setBulkShort(e.target.value)}
                className="w-full text-xs border border-[#E1DFDD] rounded-sm px-2 py-1.5 bg-white" data-testid="bulk-short-template">
                <option value="">— No change —</option>
                {shortTemplates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div className="flex items-end gap-2">
              <Button onClick={bulkAssign} disabled={!bulkFull && !bulkShort}
                className="bg-[#0F6CBD] text-white rounded-sm text-xs flex-1" data-testid="bulk-apply-btn">
                Apply to {bulkScope === "all_active" ? "All Active" : bulkScope === "domain" ? bulkScopeValue || "..." : bulkScopeValue || "..."}
              </Button>
              <Button onClick={() => setBulkOpen(false)} variant="outline" className="rounded-sm text-xs">Cancel</Button>
            </div>
          </div>
        </div>
      )}

      {/* Search + Filter */}
      <div className="mt-4 flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5E5E5E]" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, email, department, company..."
            className="pl-8 border-[#E1DFDD] rounded-sm" data-testid="staff-search-input" />
        </div>
        <div className="flex items-center gap-1 border border-[#E1DFDD] rounded-sm overflow-hidden" data-testid="filter-toggle">
          {[["all", "All"], ["active", "Active"], ["inactive", "Inactive"], ["incomplete", "Incomplete"]].map(([val, label]) => (
            <button key={val} onClick={() => setFilterMode(val)} data-testid={`filter-${val}`}
              className={`px-3 py-1.5 text-xs transition-colors ${filterMode === val
                ? "bg-[#0F6CBD] text-white" : "bg-white text-[#5E5E5E] hover:bg-[#F3F2F1]"}`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <p className="text-sm text-[#5E5E5E] mt-6">Loading directory...</p>
      ) : (
        <div className="mt-4 bg-white border border-[#E1DFDD] rounded-sm overflow-x-auto" data-testid="staff-table">
          <table className="w-full text-sm text-left min-w-[1100px]">
            <thead>
              <tr className="text-[10px] uppercase bg-[#FAFAFA] text-[#171717] border-b border-[#E1DFDD]">
                <th className="px-2 py-2.5 font-semibold w-14 text-center">Active</th>
                <th className="px-2 py-2.5 font-semibold">Name / Email</th>
                <th className="px-2 py-2.5 font-semibold">Department</th>
                <th className="px-2 py-2.5 font-semibold">Company</th>
                <th className="px-2 py-2.5 font-semibold w-48">Full Signature</th>
                <th className="px-2 py-2.5 font-semibold w-48">Short Signature</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((user) => (
                <tr key={user.id}
                  className={`border-b border-[#E1DFDD] transition-colors ${user.signature_active ? "hover:bg-[#DFF6DD]/30" : "hover:bg-[#FAFAFA] opacity-50"}`}
                  data-testid={`staff-row-${user.id}`}>
                  <td className="px-2 py-2 text-center">
                    <button onClick={() => toggleActive(user)}
                      className={`w-9 h-5 rounded-full relative transition-colors ${user.signature_active ? "bg-[#107C10]" : "bg-[#C8C6C4]"}`}
                      data-testid={`toggle-${user.id}`}>
                      <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${user.signature_active ? "left-[18px]" : "left-0.5"}`} />
                    </button>
                  </td>
                  <td className="px-2 py-2">
                    <p className="font-medium text-[#171717] text-xs leading-tight">{user.displayName}</p>
                    <p className="text-[10px] text-[#0F6CBD] font-mono">{user.mail}</p>
                    {(() => {
                      const missing = missingFieldsFor(user);
                      if (missing.length === 0) return null;
                      return (
                        <span
                          title={`Signature will be missing these lines because the fields are empty in this user's profile: ${missing.join(", ")}. Set them in Entra/Staff Directory before relying on the signature.`}
                          className="inline-flex items-center gap-1 mt-1 text-[9px] font-medium text-[#A4520E] bg-[#FFF4E5] border border-[#F2C794] rounded-sm px-1 py-0.5 leading-none"
                          data-testid={`incomplete-${user.id}`}>
                          <AlertTriangle size={9} /> Missing: {missing.join(", ")}
                        </span>
                      );
                    })()}
                  </td>
                  <td className="px-2 py-2 text-xs text-[#5E5E5E]">{user.department}</td>
                  <td className="px-2 py-2 text-xs text-[#5E5E5E]">{user.companyName}</td>
                  <td className="px-2 py-2">
                    <select value={user.assigned_full_template || ""}
                      onChange={e => assignTemplate(user.id, "full", e.target.value)}
                      disabled={!user.signature_active}
                      className={`w-full text-[11px] border rounded-sm px-1.5 py-1 ${user.signature_active ? "border-[#E1DFDD] bg-white" : "border-[#F3F2F1] bg-[#F3F2F1] text-[#C8C6C4]"}`}
                      data-testid={`full-tpl-${user.id}`}>
                      <option value="">— None —</option>
                      {optionsFor("FULL", user.assigned_full_template).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                  </td>
                  <td className="px-2 py-2">
                    <select value={user.assigned_short_template || ""}
                      onChange={e => assignTemplate(user.id, "short", e.target.value)}
                      disabled={!user.signature_active}
                      className={`w-full text-[11px] border rounded-sm px-1.5 py-1 ${user.signature_active ? "border-[#E1DFDD] bg-white" : "border-[#F3F2F1] bg-[#F3F2F1] text-[#C8C6C4]"}`}
                      data-testid={`short-tpl-${user.id}`}>
                      <option value="">— None —</option>
                      {optionsFor("SHORT", user.assigned_short_template).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && (
            <div className="p-8 text-center text-sm text-[#5E5E5E]">No users match your search.</div>
          )}
        </div>
      )}
    </div>
  );
}
